import { useTranslation } from "react-i18next";
import { appPaths } from "../../../shared/routing/appPaths";
import AppButton from "../../../ui/buttons/AppButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import ConnectorDeviceDetails from "../ConnectorDeviceDetails";
import type { PairingState } from "./pairingFlow";
import ButtonLink from "../../../ui/buttons/ButtonLink";

type FinishedPairingState = Extract<PairingState, { status: "approved" | "denied" | "alreadyClosed" }>;

interface PairingOutcomeProps {
  state: FinishedPairingState;
  onEnterOtherCode: () => void;
}

/** A code that can no longer be decided: approved or denied here, or closed before. */
export default function PairingOutcome({ state, onEnterOtherCode }: PairingOutcomeProps) {
  const { t } = useTranslation();
  const label = state.authorization.device.label;
  const message = outcomeMessage(state);
  const paired = state.authorization.state === "approved";

  return (
    <section className="space-y-4">
      <StatusMessage tone={message.tone} title={t(message.titleKey)}>
        <p>{t(message.messageKey, { label })}</p>
      </StatusMessage>
      <ConnectorDeviceDetails device={state.authorization.device} />
      <div className="flex flex-wrap items-center gap-3">
        {paired ? (
          <>
            <ButtonLink to={appPaths.connectorSetup()} variant="primary">
              {t("connectors.setup.continue")}
            </ButtonLink>
          </>
        ) : null}
        <AppButton type="button" variant="secondary" size="sm" onClick={onEnterOtherCode}>
          {t("connectors.pairing.otherCode")}
        </AppButton>
      </div>
    </section>
  );
}

interface OutcomeMessage {
  readonly tone: "success" | "info" | "warning";
  readonly titleKey: string;
  readonly messageKey: string;
}

function outcomeMessage(state: FinishedPairingState): OutcomeMessage {
  switch (state.status) {
    case "approved":
      return { tone: "success", titleKey: "connectors.pairing.approvedTitle", messageKey: "connectors.pairing.approvedMessage" };
    case "denied":
      return { tone: "info", titleKey: "connectors.pairing.deniedTitle", messageKey: "connectors.pairing.deniedMessage" };
    case "alreadyClosed":
      return {
        tone: state.closedState === "approved" ? "info" : "warning",
        titleKey: `connectors.pairing.closed.${state.closedState}.title`,
        messageKey: `connectors.pairing.closed.${state.closedState}.message`,
      };
  }
}
